import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarDays, MapPin, Users, Send, Luggage } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.luxor-limo.ch';
const API = `${BACKEND_URL}/api`;

const QuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    pickup_location: '',
    destination: '',
    passenger_count: 1,
    requested_date: '',
    requested_time: '',
    luggage_count: '',
    additional_info: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();
  const { translate, currentLanguage } = useLanguage();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const requestData = {
        ...formData,
        language: currentLanguage,
        passenger_count: parseInt(formData.passenger_count),
        message: `DEMANDE DE TARIF:
        
Départ: ${formData.pickup_location}
Destination: ${formData.destination}
Date: ${formData.requested_date}
Heure: ${formData.requested_time}
Passagers: ${formData.passenger_count}
Bagages: ${formData.luggage_count}
Service: ${formData.service_type}

Informations complémentaires: ${formData.additional_info || 'Aucune'}`,
        service: formData.service_type
      };

      const response = await axios.post(`${API}/contact`, requestData);

      if (response.data.success) {
        setSubmitted(true);
        toast({
          title: "Demande de tarif envoyée !",
          description: "Nous vous contacterons sous 2 heures avec votre tarif personnalisé.",
        });
      }
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      service_type: '',
      pickup_location: '',
      destination: '',
      passenger_count: 1,
      requested_date: '',
      requested_time: '',
      luggage_count: '',
      additional_info: ''
    });
    setSubmitted(false);
  };

  const services = [
    { value: 'Transferts Aéroport', label: translate('airportTransfers') },
    { value: 'Service Limousine', label: translate('limousineService') },
    { value: 'Événements & Plus', label: translate('eventsService') },
    { value: 'Transport Scolaire', label: translate('schoolTransport') },
    { value: 'Transport PMR', label: translate('pmrVehicles') }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black">
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center space-x-2">
              <Send className="w-6 h-6" />
              <span>Demande de Tarif Personnalisé</span>
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-black hover:bg-black/10">
              ×
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Informations Client */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Vos Informations</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Votre nom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+41 XX XXX XX XX"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              {/* Détails du Trajet */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  Détails du Trajet
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lieu de départ *
                    </label>
                    <Input
                      type="text"
                      name="pickup_location"
                      value={formData.pickup_location}
                      onChange={handleInputChange}
                      required
                      placeholder="Adresse complète de départ"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lieu d'arrivée *
                    </label>
                    <Input
                      type="text"
                      name="destination"
                      value={formData.destination}
                      onChange={handleInputChange}
                      required
                      placeholder="Adresse complète d'arrivée"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date *
                    </label>
                    <Input
                      type="date"
                      name="requested_date"
                      value={formData.requested_date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Heure *
                    </label>
                    <Input
                      type="time"
                      name="requested_time"
                      value={formData.requested_time}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Détails Passagers et Bagages */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Passagers et Bagages
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre de passagers *
                    </label>
                    <Select onValueChange={(value) => handleSelectChange('passenger_count', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Passagers" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 12, 16, 20, 50].map((num) => (
                          <SelectItem key={num} value={num.toString()}>
                            {num} {num === 1 ? 'passager' : 'passagers'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre de bagages
                    </label>
                    <Input
                      type="text"
                      name="luggage_count"
                      value={formData.luggage_count}
                      onChange={handleInputChange}
                      placeholder="ex: 2 valises, 1 sac"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de service *
                    </label>
                    <Select onValueChange={(value) => handleSelectChange('service_type', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.value} value={service.value}>
                            {service.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Informations Additionnelles */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Informations complémentaires
                </label>
                <Textarea
                  name="additional_info"
                  value={formData.additional_info}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Bagages spéciaux, demandes particulières, heure de retour, nombre d'arrêts..."
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-3 disabled:opacity-50"
              >
                <Send className="w-4 h-4 mr-2" />
                {isSubmitting ? 'Envoi en cours...' : 'Demander un Tarif Personnalisé'}
              </Button>

              {/* Logo en bas du formulaire */}
              <div className="flex justify-center mt-6 pt-4 border-t border-gray-200">
                <img
                  src="https://customer-assets.emergentagent.com/job_limo-service-1/artifacts/6t2u7cjc_Logo%20Black.jpg"
                  alt="Luxor Limousine"
                  className="h-16 w-auto opacity-80"
                />
              </div>
            </form>
          ) : (
            /* Confirmation */
            <div className="text-center">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <div className="text-6xl mb-4">✅</div>
                <h3 className="text-2xl font-bold text-green-800 mb-2">Demande Envoyée !</h3>
                <p className="text-green-700">
                  Nous avons bien reçu votre demande de tarif et vous contacterons sous 2 heures avec une offre personnalisée.
                </p>
              </div>

              <div className="space-y-4 text-left bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-semibold">Récapitulatif de votre demande :</h4>
                <div className="text-sm space-y-1">
                  <p><strong>Trajet :</strong> {formData.pickup_location} → {formData.destination}</p>
                  <p><strong>Date :</strong> {formData.requested_date} à {formData.requested_time}</p>
                  <p><strong>Passagers :</strong> {formData.passenger_count}</p>
                  <p><strong>Service :</strong> {formData.service_type}</p>
                  {formData.luggage_count && <p><strong>Bagages :</strong> {formData.luggage_count}</p>}
                </div>
              </div>

              <div className="flex space-x-4">
                <Button
                  onClick={resetForm}
                  variant="outline"
                  className="flex-1"
                >
                  Nouvelle demande
                </Button>
                <Button
                  onClick={onClose}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QuoteModal;